// InvoiceModal — ruble invoice for the reseller's downstream customer
// (Rundler-style "Сформировать счёт"). Works for a single order or a
// whole cart group: pass the line-item orders + the shared order number.
//
// Line price in rubles uses the reseller's own bookkeeping price
// (reseller_price_rub_cents) when set, else falls back to the order's
// grand total in rubles. Two outputs:
//   • "Скачать PDF" — opens a print-styled window → browser Save as PDF.
//   • "Копировать текст" — Telegram-friendly plain text to clipboard.
import { T_NEW } from "../theme.js";
import { Btn, Modal, Mono, Serif } from "./ui.jsx";
import { useToast } from "./toast.jsx";
import { useT } from "../i18n.jsx";

function lineRub(o) {
  if (o.resellerPriceRubCents != null) return o.resellerPriceRubCents / 100;
  return (o.grandTotalRubCents ?? o.totalRubCents ?? 0) / 100;
}

export function InvoiceModal({ orders, orderNumber, customerName, fromName, onClose }) {
  const t = useT();
  const toast = useToast();
  const lines = orders.map((o) => ({
    title: o.post?.title ?? t("orders.deletedPost"),
    variant: o.variant || "",
    qty: o.qty,
    rub: lineRub(o),
  }));
  const total = lines.reduce((s, l) => s + l.rub, 0);
  const dateStr = new Date().toLocaleDateString("ru-RU");
  // Prefer the real order number; for legacy orders without one (pre-0074,
  // until the backfill deploys) derive a stable fallback from the order's
  // date + a short slice of its id so the invoice is never unnumbered.
  const fallbackNum = (o) => {
    if (!o) return "";
    const d = o.placedAt ? new Date(o.placedAt * 1000) : new Date();
    const ymd = String(d.getUTCFullYear()).slice(2)
      + String(d.getUTCMonth() + 1).padStart(2, "0")
      + String(d.getUTCDate()).padStart(2, "0");
    const tail = String(o.id || "").replace(/-/g, "").slice(-4).toUpperCase();
    return tail ? `${ymd}-${tail}` : ymd;
  };
  const num = orderNumber || orders.find((o) => o.orderNumber)?.orderNumber || fallbackNum(orders[0]);

  const plainText = () => {
    const head = `${t("invoice.title")} № ${num}\n${dateStr}`;
    const to = customerName ? `\n${t("invoice.to")}: ${customerName}` : "";
    const body = lines
      .map((l, i) => `${i + 1}. ${l.title}${l.variant ? ` (${l.variant})` : ""} ×${l.qty} — ₽${l.rub.toFixed(0)}`)
      .join("\n");
    return `${head}${to}\n\n${body}\n\n${t("invoice.total")}: ₽${total.toFixed(0)}`;
  };

  const copyText = () => {
    navigator.clipboard?.writeText(plainText()).then(
      () => toast.success(t("invoice.copied")),
      () => toast.error(t("invoice.copyFail")),
    );
  };

  const downloadPdf = () => {
    const esc = (s) => String(s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const rows = lines.map((l, i) => `
      <tr>
        <td class="n">${i + 1}</td>
        <td>${esc(l.title)}${l.variant ? `<span class="v"> · ${esc(l.variant)}</span>` : ""}</td>
        <td class="c">${l.qty}</td>
        <td class="r">₽${l.rub.toFixed(0)}</td>
      </tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8">
      <title>${t("invoice.title")} ${esc(num)}</title>
      <style>
        body{font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#1a1714;padding:32px;max-width:640px;margin:0 auto}
        h1{font-size:22px;margin:0 0 4px}
        .meta{color:#6b6258;font-size:13px;margin-bottom:20px}
        .party{font-size:13px;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;font-size:14px}
        th{text-align:left;border-bottom:2px solid #1a1714;padding:8px 6px;font-size:12px;text-transform:uppercase;letter-spacing:.04em}
        td{padding:8px 6px;border-bottom:1px solid #e6e1d8}
        td.n,th.n{width:28px;color:#6b6258}
        td.c,th.c{text-align:center;width:48px}
        td.r,th.r{text-align:right;width:96px;white-space:nowrap;font-variant-numeric:tabular-nums}
        .v{color:#6b6258}
        .total{margin-top:16px;text-align:right;font-size:18px;font-weight:700}
        @media print{button{display:none}}
      </style></head><body>
      <h1>${t("invoice.title")} № ${esc(num)}</h1>
      <div class="meta">${esc(dateStr)}</div>
      ${fromName ? `<div class="party"><b>${t("invoice.from")}:</b> ${esc(fromName)}</div>` : ""}
      ${customerName ? `<div class="party"><b>${t("invoice.to")}:</b> ${esc(customerName)}</div>` : ""}
      <table><thead><tr>
        <th class="n">#</th><th>${t("invoice.item")}</th><th class="c">${t("invoice.qty")}</th><th class="r">${t("invoice.price")}</th>
      </tr></thead><tbody>${rows}</tbody></table>
      <div class="total">${t("invoice.total")}: ₽${total.toFixed(0)}</div>
      <script>window.onload=function(){setTimeout(function(){window.print()},150)}<\/script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error(t("invoice.popupBlocked")); return; }
    w.document.write(html);
    w.document.close();
  };

  return (
    <Modal title={`${t("invoice.title")} № ${num}`} onClose={onClose} width={460}>
      <div style={{ marginBottom: 14, fontSize: T_NEW.meta, color: T_NEW.ink56, fontFamily: T_NEW.fontSans }}>
        {dateStr}{customerName ? ` · ${customerName}` : ""}
      </div>

      <div style={{ border: `1px solid ${T_NEW.ink12}`, borderRadius: T_NEW.r2, overflow: "hidden", marginBottom: 16 }}>
        {lines.map((l, i) => (
          <div key={i} style={{ display: "flex", gap: 10, padding: "10px 12px",
                                borderBottom: i < lines.length - 1 ? `1px solid ${T_NEW.ink12}` : "none" }}>
            <span style={{ width: 18, color: T_NEW.ink56, fontFamily: T_NEW.fontMono, fontSize: T_NEW.meta }}>{i + 1}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Serif size={T_NEW.body} style={{ display: "block" }}>{l.title}</Serif>
              <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginTop: 2 }}>
                {l.variant ? `${l.variant} · ` : ""}×{l.qty}
              </Mono>
            </div>
            <Mono size={T_NEW.body} color={T_NEW.ink} style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
              ₽{l.rub.toFixed(0)}
            </Mono>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
                    marginBottom: 18, paddingTop: 4 }}>
        <Mono color={T_NEW.ink} style={{ fontWeight: 600 }}>{t("invoice.total")}</Mono>
        <Serif size={T_NEW.displayMd} color={T_NEW.ink} style={{ lineHeight: 1 }}>₽{total.toFixed(0)}</Serif>
      </div>

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={copyText}>{t("invoice.copy")}</Btn>
        <Btn onClick={downloadPdf}>{t("invoice.pdf")}</Btn>
      </div>
    </Modal>
  );
}
